/** Fitted equipment details reuse the animal's own geometry and skin weights. */
import type { AtelierBoots,AtelierCharm,AtelierColors } from '../../shared/atelier';
import type { AtelierDraw } from '../../shared/atelier-render';
import type { Body } from './garments';
type Vertex={p:number[];n:number[];j:number[];w:number[]};
const mixVertex=(a:Vertex,b:Vertex,t:number):Vertex=>{
  const influences=new Map<number,number>();
  for(const [v,k] of [[a,1-t],[b,t]] as const)for(let i=0;i<4;i++)influences.set(v.j[i],(influences.get(v.j[i])??0)+v.w[i]*k);
  const top=[...influences].sort((a,b)=>b[1]-a[1]).slice(0,4);while(top.length<4)top.push([0,0]);
  const sum=top.reduce((a,b)=>a+b[1],0)||1;
  return {p:a.p.map((n,i)=>n+(b.p[i]-n)*t),n:a.n.map((n,i)=>n+(b.n[i]-n)*t),j:top.map(x=>x[0]),w:top.map(x=>x[1]/sum)};
};
export function buildAccessories(body:Body,attributes:{nrm:ArrayLike<number>},boots?:AtelierBoots,charm?:AtelierCharm,colors?:AtelierColors):AtelierDraw[]{
  const draws:AtelierDraw[]=[];
  if(boots){
    const top=(body.lm.ankleR[1]+body.lm.ankleL[1])/2+(boots==='traveller'?.058:boots==='iron'?.049:boots==='quiet'?.023:.028);
    const pos:number[]=[],nrm:number[]=[],uv:number[]=[],aux:number[]=[],joints:number[]=[],weights:number[]=[],indices:number[]=[];
    const source=(id:number):Vertex=>({p:Array.from(body.pos.slice(id*3,id*3+3)),n:Array.from({length:3},(_,i)=>attributes.nrm[id*3+i]),j:Array.from(body.joints.slice(id*4,id*4+4)),w:Array.from(body.weights.slice(id*4,id*4+4))});
    const legWeight=(id:number)=>{let weight=0;for(let k=0;k<4;k++){const bone=body.sk.bones[body.joints[id*4+k]];if(bone&&/^(foot|shin|calf|lowerLeg|toe)/i.test(bone.name))weight+=body.weights[id*4+k];}return weight;};
    for(let i=0;i<body.indices.length;i+=3){
      const ids=Array.from(body.indices.slice(i,i+3));
      if(!ids.some(id=>legWeight(id)>.3)||ids.every(id=>body.pos[id*3+1]>top))continue;
      const input=ids.map(source),clipped:Vertex[]=[];
      for(let k=0;k<3;k++){
        const a=input[k],b=input[(k+1)%3],inside=a.p[1]<=top,nextInside=b.p[1]<=top;
        if(inside)clipped.push(a);if(inside!==nextInside)clipped.push(mixVertex(a,b,(top-a.p[1])/(b.p[1]-a.p[1])));
      }
      const start=pos.length/3;
      for(const v of clipped){
        // A boot cuff sits outside hose/mail, which itself sits outside the skin.
        // The sole stays fitted; only the upper third receives the overlap allowance.
        const len=Math.hypot(...v.n)||1,upper=Math.max(0,Math.min(1,(v.p[1]-(top-.045))/.035));
        const clearance=(boots==='iron'?.01:.006)+upper*.012;
        const p=v.p.map((x,k)=>x+v.n[k]/len*clearance);p[1]=Math.max(0,p[1]);
        pos.push(...p);nrm.push(...v.n);uv.push(p[0]*8,p[1]*8);aux.push(...v.p,Math.abs(top-v.p[1]));joints.push(...v.j);weights.push(...v.w);
      }
      for(let k=1;k<clipped.length-1;k++)indices.push(start,start+k,start+k+1);
    }
    const base=boots==='iron'?colors?.metal??'#82909d':colors?.leather??({worn:'#70523d',traveller:'#543b2b',quiet:'#293c47'}[boots]);
    draws.push({material:'cloth',id:`boots-${boots}`,style:{base,trim:boots==='iron'?'#c5a765':'#ad9161',accent:'#34271c',pattern:boots==='iron'?4:0,trimWidth:.005,folds:0},center:[...body.lm.pelvis,1],pos,nrm,outlineNrm:nrm,uv,aux,joints,weights,indices:new Uint32Array(indices)});
  }
  if(charm){
    const chest=body.sk.byName.get('chest')??0,c=body.lm.chest;
    let front=c[2]+body.lm.radius.torso;
    for(let i=0;i<body.pos.length/3;i++)if(Math.abs(body.pos[i*3]-c[0])<.075&&Math.abs(body.pos[i*3+1]-c[1])<.045)front=Math.max(front,body.pos[i*3+2]);
    front+=.047;
    const pos:number[]=[],nrm:number[]=[],uv:number[]=[],aux:number[]=[],joints:number[]=[],weights:number[]=[],indices:number[]=[];
    const vertex=(x:number,y:number,z:number)=>{pos.push(x,y,z);nrm.push(0,0,1);uv.push(x,y);aux.push(x,y,z,1);joints.push(chest,0,0,0);weights.push(1,0,0,0);return pos.length/3-1;};
    const quad=(a:number[],b:number[],d:number[],e:number[])=>{const s=pos.length/3;for(const p of [a,b,d,e])vertex(p[0],p[1],p[2]);indices.push(s,s+1,s+2,s,s+2,s+3);};
    // Two fine cords connect the pendant to the collar, rather than floating on the chest.
    const size=Math.max(.018,Math.min(.028,body.lm.radius.torso*.24));
    for(const side of [-1,1]){
      const a=[c[0]+side*.006,c[1]+size*.7,front],b=[c[0]+side*body.lm.radius.torso*.58,body.lm.neck[1]-.025,front-.03],w=.002;
      quad([a[0]-w,a[1],a[2]],[a[0]+w,a[1],a[2]],[b[0]+w,b[1],b[2]],[b[0]-w,b[1],b[2]]);
    }
    const center=vertex(c[0],c[1],front+.009),sides=charm==='reliquary'?4:charm==='order-seal'?10:6;
    for(let i=0;i<sides;i++){
      const angle=i/sides*Math.PI*2,next=(i+1)/sides*Math.PI*2;
      const a=vertex(c[0]+Math.sin(angle)*size,c[1]+Math.cos(angle)*size,front),b=vertex(c[0]+Math.sin(next)*size,c[1]+Math.cos(next)*size,front);
      indices.push(center,b,a);
    }
    const base=charm==='order-seal'?'#8f332b':charm==='thorn-knot'?'#5f7553':'#c2a052';
    draws.push({material:'cloth',id:`charm-${charm}`,style:{base,trim:'#e1c786',accent:'#efd69c',pattern:charm==='reliquary'?4:0,trimWidth:0,folds:0},center:[...c,1],pos,nrm,outlineNrm:nrm,uv,aux,joints,weights,indices:new Uint32Array(indices)});
  }
  return draws;
}
